import { ParsedUrlQuery } from "querystring";

import { useEffect, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Theme } from "@mui/material/styles";
import { FormType, getIsoLocaleFromCountry, Locales } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { CLARITY_PROJECT_IDS } from "../../../constants/clarity";
import { isProduction } from "../../../constants/env";
import { LocaleProvider } from "../../../contexts/LocaleContext";
import { GetCurrentUserFrontendObject } from "../../../contexts/types";
import { RedirectOnUnsupportedFormsBrowser } from "../../../features/UnsupportedBrowser/RedirectOnUnsupportedFormsBrowser";
import { useLogParamsEvent } from "../../../hooks/amplitude/useLogParamsEvent";
import { useDisableSafariBackForwardCache } from "../../../hooks/useDisableSafariBackForwardCache";
import { MinimalLayout } from "../../../layouts/MinimalLayout";
import { AmplitudeProvider } from "../../../providers/AmplitudeProvider";
import { ClarityProvider } from "../../../providers/ClarityProvider";
import { FrontendFormProvider } from "../../../providers/FrontendFormProvider";
import { PageLoadingProvider } from "../../../providers/PageLoadingProvider";
import { zeffyTheme } from "../../../theme/theme";
import { DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "../../../types/trpc";
import { DevContextCopy } from "../../DevContextCopy/DevContextCopy";
import { HubspotChatbot } from "../../HubspotChatbot/HubspotChatbot";
import { NotificationBannerHeightProvider } from "../../NotificationBanners/NotificationBannerHeightContext";
import { PublicRoute } from "../../Routes/PublicRoute";
import { UserConsent } from "../../UserConsent/UserConsent";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";
import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

export const AppFormProvider = ({
  children,
  theme,
  skipUserConsentModal,
  organization,
  formData,
  formType,
  fields,
  isEmbed,
  isFundraiser,
  shouldDisplayLogin,
  themeColor,
  SSREnabled = true,
}: {
  children: React.ReactNode;
  theme: Theme;
  themeColor: string | null;
  organization: GetCurrentUserFrontendObject["currentOrganization"];
  fields: { locale: Locales }[];
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput | null;
  formType: FormType | null;
  isEmbed?: boolean;
  isFundraiser?: boolean;
  shouldDisplayLogin?: boolean;
  skipUserConsentModal?: boolean;
  SSREnabled?: boolean;
}) => {
  useLogParamsEvent();
  useDisableSafariBackForwardCache();
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const forcedLocale = fields?.length === 1 ? fields[0].locale : undefined;
  const forcedIsoLocale = forcedLocale ? getIsoLocaleFromCountry(organization?.country, forcedLocale) : undefined;

  // Disable SSR as it creates memory leaks
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (!SSREnabled) {
      setKey(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (key === 0 && !SSREnabled) {
    return <DashboardZeffyLoader />;
  }

  return (
    <>
      {/* for router ready, see https://github.com/vercel/next.js/discussions/11484#discussioncomment-356055 */}
      <PageLoadingProvider isLoading={Boolean(!router.isReady || router.isFallback)} disableLoading>
        <ThemeProvider theme={theme || zeffyTheme}>
          <NotificationBannerHeightProvider>
            <CssBaseline />
            <AmplitudeProvider>
              <ClarityProvider project={CLARITY_PROJECT_IDS.DONOR}>
                <LocaleProvider forcedIsoLocale={forcedIsoLocale}>
                  <RedirectOnUnsupportedFormsBrowser>
                    <ZeffySnackbarProvider>
                      <DevContextCopy formId={formData?.id || null} />
                      <PublicRoute
                        layout={(props) => <MinimalLayout hideBackground {...props} />}
                        noScreenPadding={true}
                      >
                        <FrontendFormProvider
                          formData={formData}
                          organization={organization}
                          formType={formType}
                          isEmbed={isEmbed || false}
                          isFundraiser={isFundraiser || false}
                          shouldDisplayLogin={shouldDisplayLogin || false}
                          themeColor={themeColor || ""}
                        >
                          {children}
                        </FrontendFormProvider>
                      </PublicRoute>
                      {!isProduction && !hideHubspotChatbot(query, router.route) && <HubspotChatbot />}
                      <UserConsent skipModal={skipUserConsentModal} />
                    </ZeffySnackbarProvider>
                  </RedirectOnUnsupportedFormsBrowser>
                </LocaleProvider>
              </ClarityProvider>
            </AmplitudeProvider>
          </NotificationBannerHeightProvider>
        </ThemeProvider>
      </PageLoadingProvider>
    </>
  );
};

const HubspotChatflowTargets = {
  show: new Set(["o", "login", "register", "d"]),
  hide: new Set(["donation-form", "ticketing", "team", "peer-to-peer", "fundraising", "embed"]),
};

const hideHubspotChatbot = (query: ParsedUrlQuery, route: string) => {
  // Chatbot is hidden on form creation and edition
  if (Object.keys(query).length > 0 && (route.includes("/forms/edit") || route.includes("/forms/new"))) {
    return true;
  }

  // get first level path
  const path = route.split("/")[1];
  if (HubspotChatflowTargets.hide.has(path)) {
    return true;
  }

  return query.hideHubspotChatbot === "true" || !HubspotChatflowTargets.show.has(path);
};
